import { Video } from '@vimeo/gctl-api/models/components/schemas/video/Video';

/**
 * Clip (view) privacy values
 * ref: frontend/libs/gctl-api/models/components/schemas/video/Video.ts
 * ref: https://github.com/vimeows/vimeo/blob/cef8a839fddaa362694179197dbf8ec43977de0d/objects/Clip.php#L352-L366
 */
export type VideoPrivacy = Video['privacy']['view'];
export type Privacy = Video['privacy'];

/** display text "Public" */
export const PRIVACY_PUBLIC: VideoPrivacy = 'anybody';

/** display text "Password" */
export const PRIVACY_PASSWORD: VideoPrivacy = 'password';

/** display text "Unlisted" */
export const PRIVACY_UNLISTED: VideoPrivacy = 'unlisted';

/** display text "Private" */
export const PRIVACY_NOBODY: VideoPrivacy = 'nobody';

/** display text "Hide from Vimeo" */
export const PRIVACY_DISABLE: VideoPrivacy = 'disable';

/** display text "People I follow", legacy privacy option, underlining Public privacy */
export const PRIVACY_FOLLOW: VideoPrivacy = 'contacts';

/** display text "People I choose", legacy privacy option, underlining Public privacy */
export const PRIVACY_USER: VideoPrivacy = 'users';

export const STASHED_COMMENTS_KEY = 'stashed_comment';

export const STASHED_GUEST_USER_KEY = 'stashed_guest_user';

export const PUBLIC_COMMENTS_FIELDS = [
  'createdOn',
  'metadata.connections.replies.total',
  'text',
  'richtext',
  'metadata.connections.user.name',
  'metadata.connections.user.link',
  'metadata.connections.user.uri',
  'metadata.connections.user.pictures.sizes',
  'metadata.connections.user.badge',
  'metadata.connections.user.isStaffPicked',
  'metadata.connections.guestUser.name',
  'metadata.connections.guestUser.uri',
  'uri',
  'replies.createdOn',
  'replies.text',
  'replies.richtext',
  'replies.metadata.connections.user.link',
  'replies.metadata.connections.user.name',
  'replies.metadata.connections.user.uri',
  'replies.metadata.connections.user.pictures.sizes',
  'replies.metadata.connections.user.badge',
  'replies.metadata.connections.user.isStaffPicked',
  'replies.metadata.connections.guestUser.name',
  'replies.metadata.connections.guestUser.uri',
  'replies.uri',
  'replies.lastEditedOn',
  'textDecorations',
  'deletedOn',
  'lastEditedOn',
];

export const POST_PUBLIC_COMMENT_FIELDS = [
  'createdOn',
  'link',
  'richtext',
  'metadata.connections.replies.total',
  'text',
  'metadata.connections.user.name',
  'metadata.connections.user.link',
  'metadata.connections.user.uri',
  'metadata.connections.user.pictures.sizes',
  'metadata.connections.user.badge',
  'uri',
  'metadata',
  'replies.createdOn',
  'replies.link',
  'replies.text',
  'replies.richtext',
  'replies.metadata.connections.user.name',
  'replies.metadata.connections.user.link',
  'replies.metadata.connections.user.uri',
  'replies.metadata.connections.user.pictures.sizes',
  'replies.metadata.connections.user.badge',
  'replies.uri',
];

export const POST_PUBLIC_REPLY_FIELDS = [
  'createdOn',
  'link',
  'metadata.connections.replies.total',
  'text',
  'richtext',
  'metadata.connections.user.name',
  'metadata.connections.user.link',
  'metadata.connections.user.uri',
  'metadata.connections.user.pictures.sizes',
  'metadata.connections.user.badge',
  'uri',
  'metadata',
];

export const EDIT_PUBLIC_COMMENT_FIELDS = [
  'createdOn',
  'link',
  'metadata.connections.replies.total',
  'text',
  'richtext',
  'metadata.connections.user.name',
  'metadata.connections.user.link',
  'metadata.connections.user.uri',
  'metadata.connections.user.pictures.sizes',
  'metadata.connections.user.badge',
  'uri',
  'metadata',
  'replies.createdOn',
  'replies.link',
  'replies.text',
  'replies.richtext',
  'replies.metadata.connections.user.name',
  'replies.metadata.connections.user.link',
  'replies.metadata.connections.user.uri',
  'replies.metadata.connections.user.pictures.sizes',
  'replies.metadata.connections.user.badge',
  'replies.uri',
];

export const EDIT_PUBLIC_REPLY_FIELDS = [
  'createdOn',
  'link',
  'metadata.connections.replies.total',
  'text',
  'richtext',
  'metadata.connections.user.name',
  'metadata.connections.user.link',
  'metadata.connections.user.uri',
  'metadata.connections.user.pictures.sizes',
  'uri',
  'metadata',
];

export const USER_PERMISSION_FIELDS = [
  'user.uri',
  'user.metadata.interactions.follow',
  'metadata.interactions.edit',
  'metadata.interactions.delete',
  'metadata.interactions.watchlater',
  'metadata.interactions.like',
  'metadata.interactions.ask_ai_viewer',
  'metadata.interactions.flag_clip',
  'metadata.interactions.interact',
];

export const VIDEO_DATA_FIELDS = [
  'app.uri',
  'name',
  'createdTime',
  'stats.plays',
  'contentRating',
  'status',
  'spatial.stereoFormat',
  'privacy.comments',
  'privacy.view',
  'privacy.download',
  'embed.badges.dolby_vision',
  'embed.badges.hdr_10',
  'embed.badges.hdr_10_plus',
  'license',
  'descriptionHtml',
  'manageLink',
  'metadata.is_screen_record',
  'metadata.is_vimeo_create',
  'metadata.connections.privateComments.isEnabled',
  'type',
  'tags.name',
  'tags.canonical',
  'user.backgroundVideo.aspectRatio',
  'user.backgroundVideo.clipId',
  'user.backgroundVideo.configUrl',
  'user.backgroundVideo.endTime',
  'user.backgroundVideo.firstFrameUrl',
  'user.backgroundVideo.startTime',
  'user.backgroundVideo.yPosition',
  'user.uri',
  'user.link',
  'user.name',
  'user.capabilities',
  'user.pictures.sizes.link',
  'user.pictures.sizes.width',
  'user.locationDetails.formattedAddress',
  'user.skills',
  'user.metadata.connections.followers.total',
  'uploader.link',
  'uploader.name',
  'uploader.pictures.sizes',
  'metadata.interactions.view_team_members',
  'show_svv_footer_banner',
  'metadata.connections.versions.latestIncompleteVersion',
  'metadata.interactions.invite',
  'metadata.ai_content',
  'allow_guest_comments',
  'page',
  'metadata.interactions.edit',
  'metadata.interactions.canComment',
];
